import { create } from 'zustand';

import { getView, type PrismMode } from '../engine/prism.js';
import { api } from '../lib/api.js';
import { partAt, resolvePartPath, type ModelDoc, type ModelMaterial, type PaintLayerDesc } from '../lib/model-doc.js';
import { undoService, type HistorySource } from '../lib/undo.js';
import { logEditor } from './console.js';
import { useModel } from './model.js';
import { useWorkspace } from './workspace.js';

/**
 * Texture paint mode and high → low baking in the Model tab.
 *
 * The engine owns the pixels (layers, mask, stroke undo; see
 * `runtime/paint_tool.rs`) and reports its state with `paint` events. Saving
 * goes `paintSave` → engine `paintSaved` event (PNG bytes) → this store
 * writes the files and points the material's `map` / `layers` / `paintMask`
 * / `paintBlendSpace` at them with one model undo step. Bakes work the same
 * way (`bake` events).
 */

export type PaintToolName = 'draw' | 'erase' | 'blur' | 'smear' | 'clone' | 'fill' | 'mask';
export type BlendModeName =
  | 'normal'
  | 'multiply'
  | 'add'
  | 'subtract'
  | 'screen'
  | 'overlay'
  | 'darken'
  | 'lighten'
  | 'color'
  | 'softLight';
/** Color space layers and brush colors blend in (`linear` = correct light, `srgb` = legacy image-editor look). */
export type BlendSpaceName = 'linear' | 'srgb';
export type FalloffName = 'smooth' | 'sphere' | 'root' | 'sharp' | 'linear' | 'constant';
export type BakeMapName = 'normal' | 'ao' | 'curvature' | 'cavity' | 'color' | 'emission';

export const PAINT_TOOLS: Array<{ tool: PaintToolName; label: string; icon: string; title: string }> = [
  { tool: 'draw', label: 'Draw', icon: 'pencil', title: 'Paint with the brush color (Ctrl: erase)' },
  { tool: 'erase', label: 'Erase', icon: 'eraser', title: 'Erase to transparent' },
  { tool: 'blur', label: 'Soften', icon: 'smooth', title: 'Blur the texture under the brush' },
  { tool: 'smear', label: 'Smear', icon: 'move', title: 'Drag colors along the stroke' },
  { tool: 'clone', label: 'Clone', icon: 'copy', title: 'Copy from the clone source (Ctrl+click sets it)' },
  { tool: 'fill', label: 'Fill', icon: 'bucket', title: 'Fill the UV island (or a similar-color area) under the cursor' },
  { tool: 'mask', label: 'Mask', icon: 'marquee', title: 'Paint a mask that protects texels (Ctrl: unmask)' },
];

export const BLEND_MODES: Array<{ value: BlendModeName; label: string }> = [
  { value: 'normal', label: 'Normal' },
  { value: 'multiply', label: 'Multiply' },
  { value: 'add', label: 'Add' },
  { value: 'subtract', label: 'Subtract' },
  { value: 'screen', label: 'Screen' },
  { value: 'overlay', label: 'Overlay' },
  { value: 'darken', label: 'Darken' },
  { value: 'lighten', label: 'Lighten' },
  { value: 'color', label: 'Color' },
  { value: 'softLight', label: 'Soft Light' },
];

export const BLEND_SPACES: Array<{ value: BlendSpaceName; label: string; title: string }> = [
  { value: 'linear', label: 'Linear light', title: 'Decode sRGB, blend, encode back (physically correct mixes and soft edges)' },
  { value: 'srgb', label: 'sRGB (legacy)', title: 'Blend the stored sRGB values like most image editors (darker mixes)' },
];

export const FALLOFFS: Array<{ value: FalloffName; label: string }> = [
  { value: 'smooth', label: 'Smooth' },
  { value: 'sphere', label: 'Sphere' },
  { value: 'root', label: 'Root' },
  { value: 'sharp', label: 'Sharp' },
  { value: 'linear', label: 'Linear' },
  { value: 'constant', label: 'Constant' },
];

export const BAKE_MAPS: Array<{ map: BakeMapName; label: string; title: string }> = [
  { map: 'normal', label: 'Normal', title: 'Tangent-space normal map → normalMap' },
  { map: 'ao', label: 'Ambient occlusion', title: 'AO → ormMap (red channel)' },
  { map: 'color', label: 'Base color', title: 'High-poly colors → map' },
  { map: 'curvature', label: 'Curvature', title: 'Convex bright / concave dark (image only: paint masks)' },
  { map: 'cavity', label: 'Cavity', title: 'Creases dark (image only: paint masks)' },
  { map: 'emission', label: 'Emission', title: 'Emissive color → emissiveMap' },
];

export interface PaintBrush {
  /** Cursor radius in screen pixels. */
  radius: number;
  tool: PaintToolName;
  color: [number, number, number];
  secondary: [number, number, number];
  strength: number;
  falloff: FalloffName;
  spacing: number;
  blend: BlendModeName;
  normalAngle: number;
  symmetry: [boolean, boolean, boolean];
  fillMode: 'island' | 'flood';
  fillThreshold: number;
  blurSize: number;
  cloneSource: [number, number, number] | null;
  cloneLayer: number | null;
  useMask: boolean;
  invertMask: boolean;
  /** Other visible parts block the brush (off: paint through them). */
  occlusion: boolean;
}

export const DEFAULT_PAINT_BRUSH: PaintBrush = {
  radius: 40,
  tool: 'draw',
  color: [1, 1, 1],
  secondary: [0, 0, 0],
  strength: 1,
  falloff: 'smooth',
  spacing: 0.1,
  blend: 'normal',
  normalAngle: 80,
  symmetry: [false, false, false],
  fillMode: 'island',
  fillThreshold: 0.1,
  blurSize: 2,
  cloneSource: null,
  cloneLayer: null,
  useMask: true,
  invertMask: false,
  occlusion: true,
};

export interface PaintLayerState {
  name: string;
  blend: BlendModeName;
  opacity: number;
  hidden: boolean;
  /** Saved project image ('' = not saved yet). */
  image: string;
}

export interface PaintStencil {
  image: string;
  center: [number, number];
  size: [number, number];
  rotation: number;
  useColor: boolean;
}

export interface BakeOptions {
  low: string;
  high: string[];
  maps: BakeMapName[];
  size: number;
  extrusion: number;
  maxDistance: number;
  cage: string | null;
  aoSamples: number;
  margin: number;
  /** AO: the model's other visible parts shade the bake. */
  aoOtherParts: boolean;
  /** AO: the low-poly mesh itself also occludes. */
  aoIncludeLow: boolean;
}

export interface BakeState {
  running: boolean;
  progress: number;
  low: string | null;
  warning: string | null;
  error: string | null;
  /** Summary of the last finished bake. */
  result: string | null;
}

export interface PaintViewState {
  active: boolean;
  path: string | null;
  material: string | null;
  size: [number, number];
  layers: PaintLayerState[];
  activeLayer: number;
  brush: PaintBrush;
  stencil: PaintStencil | null;
  /** Engine paint history labels (oldest first / next redo first). */
  undoLabels: string[];
  redoLabels: string[];
  modified: boolean;
  warning: string | null;
  masked: boolean;
  blendSpace: BlendSpaceName;
  saving: boolean;
  /** Leave paint mode once the pending save is written. */
  endAfterSave: boolean;
}

export const INACTIVE_PAINT: PaintViewState = {
  active: false,
  path: null,
  material: null,
  size: [0, 0],
  layers: [],
  activeLayer: 0,
  brush: DEFAULT_PAINT_BRUSH,
  stencil: null,
  undoLabels: [],
  redoLabels: [],
  modified: false,
  warning: null,
  masked: false,
  blendSpace: 'linear',
  saving: false,
  endAfterSave: false,
};

/* --------------------------------------------------------------- parsing */

const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const str = (v: unknown, d = '') => (typeof v === 'string' ? v : d);
const strs = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
function vec3(v: unknown, d: [number, number, number]): [number, number, number] {
  return Array.isArray(v) && v.length >= 3 ? [num(v[0], d[0]), num(v[1], d[1]), num(v[2], d[2])] : d;
}

export function parseBrush(raw: unknown, prev: PaintBrush = DEFAULT_PAINT_BRUSH): PaintBrush {
  if (!raw || typeof raw !== 'object') return prev;
  const r = raw as Record<string, unknown>;
  const sym = Array.isArray(r.symmetry) ? r.symmetry : prev.symmetry;
  return {
    radius: num(r.radius, prev.radius),
    tool: (PAINT_TOOLS.some((t) => t.tool === r.tool) ? r.tool : prev.tool) as PaintToolName,
    color: vec3(r.color, prev.color),
    secondary: vec3(r.secondary, prev.secondary),
    strength: num(r.strength, prev.strength),
    falloff: (FALLOFFS.some((f) => f.value === r.falloff) ? r.falloff : prev.falloff) as FalloffName,
    spacing: num(r.spacing, prev.spacing),
    blend: (BLEND_MODES.some((b) => b.value === r.blend) ? r.blend : prev.blend) as BlendModeName,
    normalAngle: num(r.normalAngle, prev.normalAngle),
    symmetry: [Boolean(sym[0]), Boolean(sym[1]), Boolean(sym[2])],
    fillMode: r.fillMode === 'flood' ? 'flood' : r.fillMode === 'island' ? 'island' : prev.fillMode,
    fillThreshold: num(r.fillThreshold, prev.fillThreshold),
    blurSize: num(r.blurSize, prev.blurSize),
    cloneSource: r.cloneSource === null ? null : Array.isArray(r.cloneSource) ? vec3(r.cloneSource, [0, 0, 0]) : prev.cloneSource,
    cloneLayer: r.cloneLayer === null ? null : typeof r.cloneLayer === 'number' ? r.cloneLayer : prev.cloneLayer,
    useMask: typeof r.useMask === 'boolean' ? r.useMask : prev.useMask,
    invertMask: typeof r.invertMask === 'boolean' ? r.invertMask : prev.invertMask,
    occlusion: typeof r.occlusion === 'boolean' ? r.occlusion : prev.occlusion,
  };
}

/** `paint` engine event → store state. */
export function parsePaintEvent(raw: unknown, prev: PaintViewState): PaintViewState {
  if (!raw || typeof raw !== 'object') return prev;
  const r = raw as Record<string, unknown>;
  const brush = parseBrush(r.brush, prev.brush);
  const st = r.stencil as Record<string, unknown> | null | undefined;
  const stencil: PaintStencil | null =
    st && typeof st === 'object' && typeof st.image === 'string'
      ? {
          image: st.image,
          center: [num((st.center as number[] | undefined)?.[0], 0), num((st.center as number[] | undefined)?.[1], 0)],
          size: [num((st.size as number[] | undefined)?.[0], 256), num((st.size as number[] | undefined)?.[1], 256)],
          rotation: num(st.rotation, 0),
          useColor: st.useColor === true,
        }
      : null;
  if (r.active !== true) return { ...INACTIVE_PAINT, brush, stencil };
  const layers: PaintLayerState[] = Array.isArray(r.layers)
    ? r.layers.map((l) => {
        const o = (l ?? {}) as Record<string, unknown>;
        return {
          name: str(o.name, 'Layer'),
          blend: (BLEND_MODES.some((b) => b.value === o.blend) ? o.blend : 'normal') as BlendModeName,
          opacity: num(o.opacity, 1),
          hidden: o.hidden === true,
          image: str(o.image),
        };
      })
    : [];
  const size = Array.isArray(r.size) ? ([num(r.size[0], 0), num(r.size[1], 0)] as [number, number]) : prev.size;
  return {
    ...prev,
    active: true,
    path: str(r.path) || null,
    material: str(r.material) || null,
    size,
    layers,
    activeLayer: num(r.activeLayer, 0),
    brush,
    stencil,
    undoLabels: strs(r.undo),
    redoLabels: strs(r.redo),
    modified: r.modified === true,
    warning: typeof r.warning === 'string' ? r.warning : null,
    masked: r.masked === true,
    blendSpace: r.blendSpace === 'srgb' ? 'srgb' : 'linear',
  };
}

/* ------------------------------------------------------ stencil dragging */

/** Viewport stencil drag (Blender-style): right drag moves, Shift scales, Ctrl rotates. */
export type StencilDragMode = 'move' | 'scale' | 'rotate';

export function stencilDragMode(e: { shiftKey: boolean; ctrlKey: boolean; metaKey?: boolean }): StencilDragMode {
  if (e.ctrlKey || e.metaKey) return 'rotate';
  return e.shiftKey ? 'scale' : 'move';
}

/**
 * The stencil after dragging the pointer from `from` to `to` (engine screen
 * pixels, y down). Scale is uniform by the distance ratio to the centre;
 * rotation follows the pointer's angle around the centre (counter-clockwise
 * positive, like `rotation`; kept in -180..180).
 */
export function dragStencil(
  start: Pick<PaintStencil, 'center' | 'size' | 'rotation'>,
  mode: StencilDragMode,
  from: readonly [number, number],
  to: readonly [number, number],
): Pick<PaintStencil, 'center' | 'size' | 'rotation'> {
  const [cx, cy] = start.center;
  if (mode === 'move') return { ...start, center: [cx + to[0] - from[0], cy + to[1] - from[1]] };
  const d0 = [from[0] - cx, from[1] - cy] as const;
  const d1 = [to[0] - cx, to[1] - cy] as const;
  if (mode === 'scale') {
    const r0 = Math.hypot(d0[0], d0[1]);
    const k = r0 > 1 ? Math.hypot(d1[0], d1[1]) / r0 : 1;
    return { ...start, size: [Math.max(1, start.size[0] * k), Math.max(1, start.size[1] * k)] };
  }
  // Screen y points down: counter-clockwise on screen is a negative atan2 delta.
  const a = (Math.atan2(d1[1], d1[0]) - Math.atan2(d0[1], d0[0])) * (180 / Math.PI);
  const rotation = ((((start.rotation - a + 180) % 360) + 360) % 360) - 180;
  return { ...start, rotation: Math.round(rotation * 100) / 100 };
}

/* ----------------------------------------------------- document wiring */

export interface PaintSaved {
  modelPath: string;
  part: string;
  material: string;
  resetColor: boolean;
  map: { path: string; png: string };
  layers: Array<{ name: string; image: string; blend: BlendModeName; opacity: number; hidden: boolean; png: string }>;
  /** Protection mask image; null = nothing masked (drops `paintMask`). */
  mask: { path: string; png: string } | null;
  /** Absent in events from older engines: leave the material's value. */
  blendSpace: BlendSpaceName | null;
}

export function parsePaintSaved(raw: unknown): PaintSaved | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const map = r.map as { path?: unknown; png?: unknown } | undefined;
  const mask = r.mask as { path?: unknown; png?: unknown } | null | undefined;
  if (typeof r.material !== 'string' || !map || typeof map.path !== 'string' || typeof map.png !== 'string') return null;
  return {
    modelPath: str(r.modelPath),
    part: str(r.part),
    material: r.material,
    resetColor: r.resetColor === true,
    map: { path: map.path, png: map.png },
    layers: (Array.isArray(r.layers) ? r.layers : []).map((l) => {
      const o = (l ?? {}) as Record<string, unknown>;
      return {
        name: str(o.name, 'Layer'),
        image: str(o.image),
        blend: (BLEND_MODES.some((b) => b.value === o.blend) ? o.blend : 'normal') as BlendModeName,
        opacity: num(o.opacity, 1),
        hidden: o.hidden === true,
        png: str(o.png),
      };
    }),
    mask: mask && typeof mask.path === 'string' && typeof mask.png === 'string' ? { path: mask.path, png: mask.png } : null,
    blendSpace: r.blendSpace === 'srgb' || r.blendSpace === 'linear' ? r.blendSpace : null,
  };
}

/** Document layer entry with default fields left out (as the engine writes them). */
export function layerDesc(l: { name: string; image: string; blend: BlendModeName; opacity: number; hidden: boolean }): PaintLayerDesc {
  const d: PaintLayerDesc = { name: l.name, image: l.image };
  if (l.blend !== 'normal') d.blend = l.blend;
  if (l.opacity !== 1) d.opacity = Math.round(l.opacity * 1000) / 1000;
  if (l.hidden) d.hidden = true;
  return d;
}

/**
 * The material after a paint save: `map` = flattened PNG, `layers` = sources,
 * `paintMask` = protection mask (dropped when empty), `paintBlendSpace` (left
 * out when linear, the default).
 */
export function applyPaintSave(doc: ModelDoc, saved: PaintSaved): ModelDoc {
  const prev: ModelMaterial = doc.materials?.[saved.material] ?? {};
  const next: ModelMaterial = { ...prev, map: saved.map.path, layers: saved.layers.map(layerDesc) };
  if (saved.mask) next.paintMask = saved.mask.path;
  else delete next.paintMask;
  if (saved.blendSpace === 'srgb') next.paintBlendSpace = 'srgb';
  else if (saved.blendSpace === 'linear') delete next.paintBlendSpace;
  if (saved.resetColor) {
    const c = Array.isArray(prev.color) ? prev.color : [];
    next.color = c.length >= 4 && c[3] !== 1 ? [1, 1, 1, c[3] as number] : [1, 1, 1];
  }
  if (next.layers && next.layers.length === 0) delete next.layers;
  return { ...doc, materials: { ...(doc.materials ?? {}), [saved.material]: next } };
}

export interface BakeDone {
  low: string;
  material: string;
  maps: Array<{ map: BakeMapName | 'orm'; path: string; png: string }>;
}

export function parseBakeDone(raw: unknown): BakeDone | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.low !== 'string' || !Array.isArray(r.maps)) return null;
  return {
    low: r.low,
    material: str(r.material),
    maps: r.maps
      .map((m) => (m ?? {}) as Record<string, unknown>)
      .filter((m) => typeof m.path === 'string' && typeof m.png === 'string' && typeof m.map === 'string')
      .map((m) => ({ map: m.map as BakeMapName | 'orm', path: m.path as string, png: m.png as string })),
  };
}

function slug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9-]+/g, '_').replace(/^_+|_+$/g, '') || 'part';
}

/**
 * Wires baked maps into the low part's material (creating one when the part
 * uses the default material): normal → `normalMap`, AO → `ormMap` (unless
 * the material already has an ORM / metallic-roughness image), color →
 * `map` (replacing paint layers), emission → `emissiveMap` (a black
 * `emissive` becomes white × 1 so the map shows). Curvature and cavity stay
 * images.
 */
export function applyBakeMaps(doc: ModelDoc, bake: BakeDone): { doc: ModelDoc; wired: string[]; skipped: string[] } {
  const index = resolvePartPath(doc, bake.low);
  const part = index ? partAt(doc, index) : null;
  const wired: string[] = [];
  const skipped: string[] = [];
  if (!index || !part) return { doc, wired, skipped: bake.maps.map((m) => m.map) };
  const materials = { ...(doc.materials ?? {}) };
  let name = part.material && materials[part.material] ? part.material : '';
  let next = doc;
  if (!name) {
    const base = `${slug(bake.low.split('/').pop() ?? 'part')}_baked`;
    name = base;
    for (let k = 2; materials[name]; k++) name = `${base}${k}`;
    materials[name] = { ...(materials[part.material ?? ''] ?? materials.default ?? {}) };
    next = setPartMaterial(doc, index, name);
  }
  const mat: ModelMaterial = { ...materials[name] };
  for (const m of bake.maps) {
    if (m.map === 'normal') {
      mat.normalMap = m.path;
      wired.push('normal');
    } else if (m.map === 'orm') {
      if (mat.ormMap && mat.ormMap !== m.path) skipped.push('ao');
      else if (mat.metallicRoughnessMap) skipped.push('ao');
      else {
        mat.ormMap = m.path;
        wired.push('ao');
      }
    } else if (m.map === 'color') {
      mat.map = m.path;
      delete mat.layers;
      wired.push('color');
    } else if (m.map === 'emission') {
      mat.emissiveMap = m.path;
      // The map multiplies `emissive`, which defaults to black.
      if (!mat.emissive || mat.emissive.slice(0, 3).every((c) => !c)) {
        mat.emissive = [1, 1, 1];
        delete mat.emissiveIntensity;
      }
      wired.push('emission');
    }
  }
  materials[name] = mat;
  return { doc: { ...next, materials }, wired, skipped };
}

function setPartMaterial(doc: ModelDoc, index: readonly number[], material: string): ModelDoc {
  const parts = [...(doc.parts ?? [])];
  let list = parts;
  for (let i = 0; i < index.length; i++) {
    const at = index[i]!;
    const copy = { ...list[at]! };
    list[at] = copy;
    if (i === index.length - 1) copy.material = material;
    else {
      copy.children = [...(copy.children ?? [])];
      list = copy.children;
    }
  }
  return { ...doc, parts };
}

/* ------------------------------------------------------------------- IO */

export interface PaintIO {
  /** Writes a base64 file into the project. */
  writeBase64(projectId: number, path: string, base64: string): Promise<void>;
}

const httpIO: PaintIO = {
  async writeBase64(projectId, path, base64) {
    await api.put(`/projects/${projectId}/file`, { path, content: base64, encoding: 'base64' });
  },
};

let io: PaintIO = httpIO;

/** Replace the file writer (tests). Pass null to restore HTTP. */
export function setPaintIO(next: PaintIO | null): void {
  io = next ?? httpIO;
}

async function writeImages(files: Array<{ path: string; png: string }>): Promise<boolean> {
  const pid = useWorkspace.getState().projectId;
  if (!pid) return false;
  try {
    for (const f of files) await io.writeBase64(pid, f.path, f.png);
  } catch (error) {
    logEditor('error', `Could not write textures: ${error instanceof Error ? error.message : String(error)}`);
    return false;
  }
  for (const f of files) for (const mode of ['scene', 'model', 'play'] as PrismMode[]) void getView(mode)?.fileChanged(f.path);
  return true;
}

/* ---------------------------------------------------------------- store */

async function modelOp(name: string, params: Record<string, unknown> = {}): Promise<boolean> {
  const outcome = await getView('model')?.modelCommand({ op: 'modelOp', name, ...params });
  if (outcome && !outcome.ok && outcome.error) {
    logEditor('warn', outcome.error);
    return false;
  }
  return Boolean(outcome?.ok);
}

export interface PaintStore extends PaintViewState {
  bake: BakeState;
  /** Interactive radius / strength drag (F / Shift+F). */
  radial: { kind: 'radius' | 'strength'; startX: number; start: number } | null;
  begin: (path?: string, size?: number) => void;
  /** Leave paint mode; asks to save unsaved strokes. */
  end: (save?: boolean) => void;
  setBrush: (patch: Partial<PaintBrush>) => void;
  swapColors: () => void;
  layer: (action: string, params?: Record<string, unknown>) => void;
  save: () => void;
  undo: () => void;
  redo: () => void;
  mask: (action: 'clear' | 'fill' | 'invert') => void;
  /** Color space the layers blend in (one paint undo step; saved on the material). */
  setBlendSpace: (space: BlendSpaceName) => void;
  setStencil: (patch: Partial<PaintStencil> | null) => void;
  sample: () => void;
  startRadial: (kind: 'radius' | 'strength', x: number) => void;
  fromEngine: (raw: unknown) => void;
  savedFromEngine: (raw: unknown) => Promise<void>;
  startBake: (options: BakeOptions) => void;
  cancelBake: () => void;
  bakeFromEngine: (raw: unknown) => Promise<void>;
}

const INITIAL_BAKE: BakeState = { running: false, progress: 0, low: null, warning: null, error: null, result: null };

export const usePaint = create<PaintStore>((set, get) => ({
  ...INACTIVE_PAINT,
  bake: INITIAL_BAKE,
  radial: null,

  begin: (path, size) => {
    const params: Record<string, unknown> = {};
    if (path) params.path = path;
    if (size) params.size = size;
    getView('model')?.modelCommand({ op: 'modelOp', name: 'paintBrush', ...get().brush });
    modelOp('paintBegin', params);
  },
  end: (save) => {
    const s = get();
    if (!s.active) return;
    const keep = save ?? (s.modified && typeof window !== 'undefined' ? window.confirm('Save the painted texture before leaving Texture Paint?') : false);
    if (keep && s.modified) {
      set({ endAfterSave: true });
      get().save();
      return;
    }
    modelOp('paintEnd');
  },
  setBrush: (patch) => {
    set({ brush: { ...get().brush, ...patch } });
    modelOp('paintBrush', patch as Record<string, unknown>);
  },
  swapColors: () => {
    const b = get().brush;
    get().setBrush({ color: b.secondary, secondary: b.color });
  },
  layer: (action, params = {}) => void modelOp('paintLayer', { action, ...params }),
  save: () => {
    if (!get().active) return;
    set({ saving: true });
    void modelOp('paintSave').then((ok) => {
      if (!ok) set({ saving: false, endAfterSave: false });
    });
  },
  undo: () => void modelOp('paintUndo'),
  redo: () => void modelOp('paintRedo'),
  mask: (action) => void modelOp('paintMask', { action }),
  setBlendSpace: (space) => void modelOp('paintLayer', { action: 'blendSpace', value: space }),
  setStencil: (patch) => {
    if (patch === null) modelOp('paintStencil', { image: null });
    else {
      // Optimistic: the overlay and popover follow drags before the engine echoes.
      const cur = get().stencil;
      if (cur && (patch.image === undefined || patch.image === cur.image)) set({ stencil: { ...cur, ...patch } });
      modelOp('paintStencil', patch as Record<string, unknown>);
    }
  },
  sample: () => void modelOp('paintSample'),
  startRadial: (kind, x) => {
    const b = get().brush;
    set({ radial: { kind, startX: x, start: kind === 'radius' ? b.radius : b.strength } });
  },

  fromEngine: (raw) => {
    const prev = get();
    const next = parsePaintEvent(raw, prev);
    set(next);
    syncUndoSource(next.active);
    const key = (s: PaintViewState) => JSON.stringify([s.undoLabels, s.redoLabels]);
    if (key(next) !== key(prev)) notifyUndo();
  },

  savedFromEngine: async (raw) => {
    const saved = parsePaintSaved(raw);
    if (!saved) return;
    const files = [saved.map, ...saved.layers.map((l) => ({ path: l.image, png: l.png })), ...(saved.mask ? [saved.mask] : [])];
    const ok = await writeImages(files);
    set({ saving: false });
    if (!ok) {
      set({ endAfterSave: false });
      return;
    }
    const m = useModel.getState();
    if (m.doc && m.path === saved.modelPath) m.update(applyPaintSave(m.doc, saved));
    logEditor('info', `Saved ${saved.map.path} (${saved.layers.length} layer${saved.layers.length === 1 ? '' : 's'})`);
    if (get().endAfterSave) {
      set({ endAfterSave: false });
      modelOp('paintEnd');
    }
  },

  startBake: (o) => {
    set({ bake: { ...INITIAL_BAKE, running: true, low: o.low } });
    const params: Record<string, unknown> = {
      low: o.low,
      high: o.high,
      maps: o.maps,
      size: o.size,
      extrusion: o.extrusion,
      maxDistance: o.maxDistance,
      aoSamples: o.aoSamples,
      margin: o.margin,
      aoOtherParts: o.aoOtherParts,
      aoIncludeLow: o.aoIncludeLow,
    };
    if (o.cage) params.cage = o.cage;
    const view = getView('model');
    if (!view) {
      set({ bake: { ...INITIAL_BAKE, error: 'The engine view is not ready.' } });
      return;
    }
    void view.modelCommand({ op: 'modelOp', name: 'bake', ...params }).then((outcome) => {
      if (!outcome.ok) set({ bake: { ...INITIAL_BAKE, error: outcome.error ?? 'The bake did not start.' } });
    });
  },
  cancelBake: () => {
    modelOp('bakeCancel');
    set({ bake: { ...get().bake, running: false } });
  },
  bakeFromEngine: async (raw) => {
    const r = (raw ?? {}) as Record<string, unknown>;
    const bake = get().bake;
    if (r.state === 'running') {
      set({
        bake: { ...bake, running: true, progress: num(r.progress, 0), low: str(r.low) || bake.low, warning: typeof r.warning === 'string' ? r.warning : bake.warning },
      });
      return;
    }
    if (r.state === 'cancelled' || r.state === 'error') {
      set({ bake: { ...bake, running: false, error: r.state === 'error' ? str(r.message, 'Bake failed') : null } });
      return;
    }
    if (r.state !== 'done') return;
    const done = parseBakeDone(raw);
    if (!done) return;
    set({ bake: { ...bake, progress: 1 } });
    const ok = await writeImages(done.maps);
    if (!ok) {
      set({ bake: { ...get().bake, running: false, error: 'Could not write the baked images.' } });
      return;
    }
    const m = useModel.getState();
    let summary = `Baked ${done.maps.filter((x) => x.map !== 'orm').length} map(s) into textures/`;
    if (m.doc) {
      const { doc, wired, skipped } = applyBakeMaps(m.doc, done);
      if (wired.length) m.update(doc);
      summary = `Baked ${done.maps.filter((x) => x.map !== 'orm').map((x) => x.map).join(', ')}` + (wired.length ? `; wired ${wired.join(', ')} into the material` : '');
      if (skipped.length) summary += ` (kept the existing ORM map: AO saved as an image)`;
    }
    logEditor('info', summary);
    set({ bake: { ...get().bake, running: false, progress: 1, result: summary } });
  },
}));

/* ----------------------------------------------------- undo integration */

const undoListeners = new Set<() => void>();
let undoOff: (() => void) | null = null;

function notifyUndo(): void {
  for (const l of [...undoListeners]) l();
}

/** Paint strokes as an Undo History source while paint mode is on. */
export const paintHistorySource: HistorySource = {
  id: 'model.paint',
  label: 'Texture Paint',
  view: 'model',
  snapshot: () => {
    const s = usePaint.getState();
    return { past: s.undoLabels.map((label) => ({ label })), future: s.redoLabels.map((label) => ({ label })) };
  },
  undo: () => usePaint.getState().undo(),
  redo: () => usePaint.getState().redo(),
  subscribe: (listener) => {
    undoListeners.add(listener);
    return () => undoListeners.delete(listener);
  },
};

function syncUndoSource(active: boolean): void {
  if (active && !undoOff) undoOff = undoService.register(paintHistorySource);
  else if (!active && undoOff) {
    undoOff();
    undoOff = null;
  }
}
